import { defineStore } from 'pinia';
import { authUseCases, UserEntity } from '@/modules/auth';
import { httpService } from '@/services/HttpService';
import { useWishlistStore } from './wishlist';
import { initializeLocale, i18n } from '@/plugins/i18n';
import type { LoginData, RegisterData, ForgotPasswordData, ResetPasswordData } from '@/types';
import { getApiErrorMessage } from '@/utils/apiError';
import type { PermissionSlug } from '@/constants/permissions';
import { startOAuthLogin } from '@/services/OAuthLoginNavigation';
import { checkSilentOAuthLogin } from '@/services/OAuthSilentLogin';

const silentLoginAttempts = new WeakMap<object, Promise<void>>();

type AuthState = {
  user: UserEntity | null;
  token: string | null;
  authServiceAccessToken: string | null;
  loading: boolean;
  error: string | null;
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: null,
    authServiceAccessToken: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    currentUser: (state) => state.user,
    permissionSet: (state): ReadonlySet<PermissionSlug> => {
      return new Set(state.user?.permissions ?? []);
    },
    hasPermission() {
      return (permission: string) => this.permissionSet.has(permission as PermissionSlug);
    },
  },

  actions: {
    initiateSilentOAuthLogin(): Promise<void> {
      if (typeof window === 'undefined' || this.isAuthenticated) return Promise.resolve();
      const existing = silentLoginAttempts.get(this);
      if (existing) return existing;

      let navigatingVisibly = false;
      const attempt = (async () => {
        try {
          const response = await checkSilentOAuthLogin();
          const token = response.data.auth_service_access_token;
          this.updateUser(UserEntity.fromApi(response.data.user));
          this.token = token;
          this.authServiceAccessToken = token;
          httpService.setAuthToken(token);
          httpService.setAuthServiceAccessToken(token);
          httpService.setOAuthLocalToken(null);
        } catch {
          // A fresh visible state and nonce are prepared by the existing navigation path.
          await this.initiateOAuthLogin();
          navigatingVisibly = true;
        }
      })();
      silentLoginAttempts.set(this, attempt);
      const release = () => {
        if (!navigatingVisibly) silentLoginAttempts.delete(this);
      };
      void attempt.then(release, release);
      return attempt;
    },

    async login(credentials: LoginData) {
      this.loading = true;
      this.error = null;
      try {
        const result = await authUseCases.login.execute(credentials);
        this.user = result.user;
        this.token = result.token;
        httpService.setAuthToken(this.token);
        this.authServiceAccessToken = null;
        httpService.setAuthServiceAccessToken(null);
        httpService.setOAuthLocalToken(null);

        const resolvedLocale = initializeLocale(this.user?.locale ?? null);
        this.persistLocaleForUserWithoutPreference(resolvedLocale);

        return result;
      } catch (error: unknown) {
        this.error = getApiErrorMessage(error, i18n.global.t('auth.messages.loginFailed'));
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async register(data: RegisterData) {
      this.loading = true;
      this.error = null;
      try {
        const user = await authUseCases.register.execute(data);
        // Auto-login after register
        await this.login({ email: data.email, password: data.password });
        return user;
      } catch (error: unknown) {
        this.error = getApiErrorMessage(error, i18n.global.t('auth.messages.registrationFailed'));
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      if (this.token) {
        try {
          await authUseCases.logout.execute(this.authServiceAccessToken ?? undefined);
        } catch (error) {
          console.error('Logout API call failed:', error);
        }
      }
      this.clearAuth();
    },

    async initiateOAuthLogin() {
      this.loading = true;
      this.error = null;

      try {
        await startOAuthLogin();
      } catch (error: unknown) {
        this.error = getApiErrorMessage(error, i18n.global.t('auth.messages.oauthStartFailed'));
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async forgotPassword(data: ForgotPasswordData) {
      this.loading = true;
      this.error = null;
      try {
        return await authUseCases.forgotPassword.execute(data);
      } catch (error: unknown) {
        this.error = getApiErrorMessage(error, i18n.global.t('auth.messages.passwordResetRequestFailed'));
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword(data: ResetPasswordData) {
      this.loading = true;
      this.error = null;
      try {
        const result = await authUseCases.resetPassword.execute(data);
        this.clearAuth();
        return result;
      } catch (error: unknown) {
        this.error = getApiErrorMessage(error, i18n.global.t('auth.messages.passwordResetFailed'));
        throw error;
      } finally {
        this.loading = false;
      }
    },

    updateUser(userEntity: UserEntity) {
      this.user = userEntity;
    },

    persistLocaleForUserWithoutPreference(resolvedLocale: string) {
      if (!this.user || this.user.locale !== null) {
        return;
      }

      void httpService
        .put<{ message: string; locale: string }>('/user/locale', { locale: resolvedLocale })
        .then(() => {
          if (!this.user) {
            return;
          }

          this.user = UserEntity.fromApi({
            ...this.user.toPlain(),
            locale: resolvedLocale,
          });
        })
        .catch((error) => {
          console.warn('[auth] Failed to persist locale after login:', error);
        });
    },

    async loadTokenFromStorage() {
      const savedToken = httpService.getAuthToken();
      const savedAuthServiceAccessToken = httpService.getAuthServiceAccessToken();

      this.authServiceAccessToken = savedAuthServiceAccessToken;

      if (savedToken) {
        this.token = savedToken;
        try {
          // Use repository directly for getCurrentUser
          const { HttpAuthRepository } = await import('@/modules/auth/infrastructure/repositories/HttpAuthRepository');
          const repo = new HttpAuthRepository();
          this.user = await repo.getCurrentUser();

          const resolvedLocale = initializeLocale(this.user?.locale ?? null);
          this.persistLocaleForUserWithoutPreference(resolvedLocale);
        } catch {
          this.clearAuth();
        }
      }
    },

    clearAuth() {
      this.user = null;
      this.token = null;
      this.authServiceAccessToken = null;
      this.error = null;
      httpService.setAuthToken(null);
      httpService.setAuthServiceAccessToken(null);
      httpService.setOAuthLocalToken(null);

      // Reset wishlist store on logout
      const wishlistStore = useWishlistStore();
      wishlistStore.reset();
    }
  },
});
